'use client'

import Link from 'next/link'
import { motion } from 'framer-motion'
import { BackgroundBeams } from '../../_components/_ui/background-beams'
import { FaCode, FaDatabase, FaCogs, FaShareAlt, FaServer, FaShieldAlt, FaBoxOpen, FaRocket } from 'react-icons/fa'

const documentationPages = [
    { 
        name: 'Architecture Overview',
        href: '/support/documentation/architecture',
        description: "Complete tech stack breakdown: Next.js 14, Supabase, AWS Amplify, and deployment pipeline",
        icon: FaRocket,
        color: "bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/20"
    },
    { 
        name: 'Block System',
        href: '/support/documentation/blocks',
        description: "Deep dive into the expandable block system with 12+ content types and custom rendering",
        icon: FaBoxOpen,
        color: "bg-purple-500/10 hover:bg-purple-500/20 border-purple-500/20"
    },
    { 
        name: 'API Documentation',
        href: '/support/documentation/api',
        description: "REST endpoints, content delivery, and how to integrate with external systems",
        icon: FaCode,
        color: "bg-green-500/10 hover:bg-green-500/20 border-green-500/20"
    },
    { 
        name: 'Database Schema',
        href: '/support/documentation/database',
        description: "PostgreSQL structure, relationships, and data modeling for profiles, pages, and blocks",
        icon: FaDatabase,
        color: "bg-indigo-500/10 hover:bg-indigo-500/20 border-indigo-500/20"
    },
    { 
        name: 'Authentication & Roles',
        href: '/support/documentation/auth',
        description: "User authentication, role-based permissions, subscription tiers, and security policies",
        icon: FaShieldAlt,
        color: "bg-red-500/10 hover:bg-red-500/20 border-red-500/20"
    },
    { 
        name: 'Sharing & Security',
        href: '/support/documentation/sharing',
        description: "QR codes, password protection, link shortening, and content visibility controls",
        icon: FaShareAlt,
        color: "bg-cyan-500/10 hover:bg-cyan-500/20 border-cyan-500/20"
    },
    { 
        name: 'Infrastructure & Deployment',
        href: '/support/documentation/infrastructure',
        description: "AWS Amplify setup, GitHub Actions CI/CD, hosting, and scaling considerations",
        icon: FaServer,
        color: "bg-orange-500/10 hover:bg-orange-500/20 border-orange-500/20"
    },
    { 
        name: 'Advanced Features',
        href: '/support/documentation/advanced',
        description: "Real-time updates, analytics, admin tools, and platform extensibility",
        icon: FaCogs,
        color: "bg-yellow-500/10 hover:bg-yellow-500/20 border-yellow-500/20"
    },
]

export default function DocumentationIndex() {
    return (
        <div className="min-h-screen w-full relative flex flex-col">
            <BackgroundBeams />
            <div className="relative z-10 flex-grow">
                {/* Hero Section */}
                <div className="pt-16 pb-8 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto">
                        <motion.h1 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="font-space-grotesk text-3xl sm:text-4xl font-bold text-white leading-tight"
                        >
                            Developer Documentation
                        </motion.h1>
                        <motion.p 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="text-lg text-gray-300 mt-4 max-w-3xl"
                        >
                            Comprehensive technical documentation for Autofocus - the platform that rebuilds social media 
                            with focus, depth, and an expandable block system. Explore our architecture, APIs, and 
                            implementation details.
                        </motion.p>
                        
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="mt-6 p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/20"
                        >
                            <h3 className="text-lg font-semibold text-white mb-2">
                                What is Autofocus?
                            </h3>
                            <p className="text-gray-300 text-sm">
                                Autofocus is a content platform designed for creators who need more than traditional social media. 
                                It's "Linktree with Adderall" - infinitely scalable, pluggable, and built around an expandable 
                                block system that grows with your needs. Share everything from Lightroom presets to 4Runner mods, 
                                camera gear, and detailed guides in one unified, authenticated space.
                            </p>
                        </motion.div>
                    </div>
                </div>

                {/* Documentation Grid */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {documentationPages.map((doc, index) => (
                            <motion.div
                                key={doc.name}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <Link href={doc.href}>
                                    <div className={`p-6 rounded-xl border backdrop-blur-sm transition-all duration-300 hover:scale-[1.02] ${doc.color}`}>
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0">
                                                <doc.icon className="text-2xl" />
                                            </div>
                                            <div className="flex-grow">
                                                <h2 className="text-lg font-semibold text-white mb-2">
                                                    {doc.name}
                                                </h2>
                                                <p className="text-sm text-gray-300">
                                                    {doc.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>

                    {/* Quick Access Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.8 }}
                        className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6"
                    >
                        <div className="p-6 rounded-xl border border-green-500/20 bg-green-500/10 backdrop-blur-sm">
                            <h3 className="text-lg font-semibold text-white mb-2">
                                🚀 Quick Start
                            </h3>
                            <p className="text-sm text-gray-300 mb-4">
                                New to Autofocus? Start with our Architecture Overview to understand the full stack.
                            </p>
                            <Link href="/support/documentation/architecture" className="text-green-400 hover:text-green-300 text-sm font-medium">
                                Get Started →
                            </Link>
                        </div>
                        
                        <div className="p-6 rounded-xl border border-purple-500/20 bg-purple-500/10 backdrop-blur-sm">
                            <h3 className="text-lg font-semibold text-white mb-2">
                                🔌 API Integration
                            </h3>
                            <p className="text-sm text-gray-300 mb-4">
                                Building something that connects with Autofocus? Check our API documentation.
                            </p>
                            <Link href="/support/documentation/api" className="text-purple-400 hover:text-purple-300 text-sm font-medium">
                                Explore APIs →
                            </Link>
                        </div>
                        
                        <div className="p-6 rounded-xl border border-blue-500/20 bg-blue-500/10 backdrop-blur-sm">
                            <h3 className="text-lg font-semibold text-white mb-2">
                                🧩 Custom Blocks
                            </h3>
                            <p className="text-sm text-gray-300 mb-4">
                                Want to understand how our expandable block system works? Dive into the details.
                            </p>
                            <Link href="/support/documentation/blocks" className="text-blue-400 hover:text-blue-300 text-sm font-medium">
                                Learn Blocks →
                            </Link>
                        </div>
                    </motion.div>

                    {/* Tech Highlights */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 1.0 }}
                        className="mt-16 p-8 rounded-xl border border-neutral-700 bg-neutral-900/50 backdrop-blur-sm"
                    >
                        <h3 className="text-xl font-semibold text-white mb-6 text-center">
                            Platform Highlights
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                            <div>
                                <div className="text-2xl font-bold text-indigo-400">12+</div>
                                <div className="text-sm text-gray-400">Block Types</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-green-400">Real-time</div>
                                <div className="text-sm text-gray-400">Updates</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-purple-400">REST</div>
                                <div className="text-sm text-gray-400">API Access</div>
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-orange-400">AWS</div>
                                <div className="text-sm text-gray-400">Infrastructure</div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    )
}